import { cn } from "@/lib/classNames";

/** Verdant + russet stops promoted into UI semantics (see `/design-system/colors` scheme section). */
export const STUDIO_COLOR_HEX = {
  /** Primary accent — moss from Verdant gold (42% stop). */
  accentPrimary: "#3e664d",
  /** Secondary accent — terracotta from Russet archive (46% stop). */
  accentSecondary: "#9c4c32",
  verdantDeep: "#23443a",
  verdantGold: "#d8b35f",
  russetDeep: "#5d2f26",
  russetGold: "#f0c26f",
  ink: "#0a0a0a",
  paper: "#fffaf1",
  buttonTextOnInk: "#fff7ec",
} as const;

export const STUDIO_ACCENT_SWATCHES: readonly {
  id: string;
  label: string;
  hex: string;
  role: string;
}[] = [
  {
    id: "accent-primary",
    label: "Primary accent",
    hex: STUDIO_COLOR_HEX.accentPrimary,
    role: "Moss — focus rings, primary accent borders, verdant emphasis.",
  },
  {
    id: "accent-secondary",
    label: "Secondary accent",
    hex: STUDIO_COLOR_HEX.accentSecondary,
    role: "Terracotta — secondary borders, warnings, russet emphasis.",
  },
  {
    id: "verdant-deep",
    label: "Verdant deep",
    hex: STUDIO_COLOR_HEX.verdantDeep,
    role: "Deep pine — text on light fills, dark green chrome.",
  },
  {
    id: "verdant-gold",
    label: "Harvest gold",
    hex: STUDIO_COLOR_HEX.verdantGold,
    role: "Warm gold — chips, highlights paired with green family.",
  },
  {
    id: "russet-deep",
    label: "Russet deep",
    hex: STUDIO_COLOR_HEX.russetDeep,
    role: "Burnt umber — deep red-brown shade.",
  },
  {
    id: "russet-gold",
    label: "Warm gold",
    hex: STUDIO_COLOR_HEX.russetGold,
    role: "Sunny gold — chips, highlights paired with russet family.",
  },
];

/** Shared sizing / focus for Monolith controls (compose with button role classes). */
export const MONOLITH_BUTTON_FRAME_CLASS =
  "inline-flex min-w-[11rem] items-center justify-center px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3e664d]";

/** Dense rectangular controls (no radius) where full `frame` min-width is too much. */
export const MONOLITH_BUTTON_CHROME_CLASS_NAMES = {
  frame: MONOLITH_BUTTON_FRAME_CLASS,
  toolbar:
    "inline-flex items-center justify-center px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors disabled:cursor-not-allowed disabled:opacity-40",
  compact:
    "inline-flex items-center justify-center px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] transition-colors disabled:cursor-not-allowed disabled:opacity-60",
} as const;

export const MONOLITH_BOARD_STYLE = {
  shellClassName:
    "min-h-screen bg-[linear-gradient(180deg,#f7f3eb_0%,#f0eae0_60%,#e7dfd2_100%)] text-neutral-950",
  panelClassName:
    "border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[16px_16px_0_rgba(19,19,19,0.18)]",
  /** Dashboard list hero — smaller offset than `panelClassName` so the hard shadow does not dominate vertically. */
  dashboardHeaderPanelClassName:
    "border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[8px_8px_0_rgba(19,19,19,0.16)]",
  switcherClassName: "border-2 border-neutral-950 bg-[#fffaf1] text-neutral-950",
  switcherActiveClassName: "border-neutral-950 bg-neutral-950 text-[#fff7ec]",
  switcherIdleClassName:
    "border-neutral-950 bg-[#fffaf1] text-neutral-950 hover:bg-[#fff2d9]",
  /** Default CTA — green affirmative action. */
  primaryButtonClassName:
    "border-[3px] border-neutral-950 bg-[#3e664d] text-[#fff7ec] hover:bg-[#23443a] shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_0_rgba(0,0,0,0.08)] active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.25)] active:translate-y-px",
  secondaryButtonClassName:
    "border-[3px] border-neutral-950 bg-[#fff2d9] text-neutral-950 hover:bg-[#ffe7bc] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_1px_0_rgba(0,0,0,0.06)] active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.15)] active:translate-y-px",
  /** Low-emphasis back-out action. */
  cancelButtonClassName:
    "border-[3px] border-neutral-950 bg-transparent text-neutral-950 hover:bg-[#fff2d9] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_1px_0_rgba(0,0,0,0.06)] active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.15)] active:translate-y-px",
  mutedButtonClassName:
    "border-[3px] border-neutral-950 bg-transparent text-neutral-950 hover:bg-[#fff2d9] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_1px_0_rgba(0,0,0,0.06)] active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.15)] active:translate-y-px",
  /** Irreversible or warning action. */
  destructiveButtonClassName:
    "border-[3px] border-neutral-950 bg-[#9c4c32] text-[#fff7ec] hover:bg-[#5d2f26] shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_0_rgba(0,0,0,0.08)] active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.25)] active:translate-y-px",
  accentPrimarySolidButtonClassName:
    "border-[3px] border-neutral-950 bg-[#3e664d] text-[#fff7ec] hover:bg-[#23443a] shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_0_rgba(0,0,0,0.08)] active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.25)] active:translate-y-px",
  accentSecondarySolidButtonClassName:
    "border-[3px] border-neutral-950 bg-[#9c4c32] text-[#fff7ec] hover:bg-[#5d2f26] shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_0_rgba(0,0,0,0.08)] active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.25)] active:translate-y-px",
  backdropAccentClassName: "border-4 border-[#9c4c32]",
  statusClassName: "border-2 border-neutral-950 bg-[#fff2d9] text-neutral-950",
  badgeClassName: "border-2 border-neutral-950 bg-[#fff2d9] text-neutral-950",
} as const;

export const MONOLITH_BUTTON_VARIANT_CLASS_NAMES = {
  primary: MONOLITH_BOARD_STYLE.primaryButtonClassName,
  destructive: MONOLITH_BOARD_STYLE.destructiveButtonClassName,
  cancel: MONOLITH_BOARD_STYLE.cancelButtonClassName,
  secondary: MONOLITH_BOARD_STYLE.secondaryButtonClassName,
  muted: MONOLITH_BOARD_STYLE.mutedButtonClassName,
  accentPrimary: MONOLITH_BOARD_STYLE.accentPrimarySolidButtonClassName,
  accentSecondary: MONOLITH_BOARD_STYLE.accentSecondarySolidButtonClassName,
} as const;

export type ButtonVariant = keyof typeof MONOLITH_BUTTON_VARIANT_CLASS_NAMES;
export type ButtonChrome = keyof typeof MONOLITH_BUTTON_CHROME_CLASS_NAMES;

export function getButtonClassName({
  variant,
  chrome = "frame",
  className,
}: {
  variant: ButtonVariant;
  chrome?: ButtonChrome;
  className?: string;
}): string {
  return cn(
    MONOLITH_BUTTON_CHROME_CLASS_NAMES[chrome],
    MONOLITH_BUTTON_VARIANT_CLASS_NAMES[variant],
    className,
  );
}

/** Vertical wash behind the whole Monolith Board shell. */
export const MONOLITH_SHELL_STOPS = [
  { label: "Top", pct: 0, hex: "#f7f3eb" },
  { label: "Mid", pct: 60, hex: "#f0eae0" },
  { label: "Bottom", pct: 100, hex: "#e7dfd2" },
] as const;

export const MONOLITH_SHELL_GRADIENT_CSS =
  "linear-gradient(180deg,#f7f3eb 0%,#f0eae0 60%,#e7dfd2 100%)";

/** Flat tokens referenced by Monolith Board (and a few dashboard accents). */
export const MONOLITH_UI_COLORS = [
  { id: "panel-paper", label: "Panel / card paper", hex: "#fffaf1" },
  { id: "primary-button-text", label: "Primary button text", hex: "#fff7ec" },
  { id: "secondary-fill", label: "Secondary / badge fill", hex: "#fff2d9" },
  { id: "secondary-hover", label: "Secondary hover", hex: "#ffe7bc" },
  { id: "map-row", label: "Map list row", hex: "#fff7ec" },
  { id: "map-row-hover", label: "Map list row hover", hex: "#ffeecd" },
  { id: "ink", label: "Ink (matches Tailwind neutral-950)", hex: "#0a0a0a" },
] as const;

/** Linear overlays stacked on top of campaign hero gradients in the UI. */
export const CAMPAIGN_IMAGE_OVERLAYS = [
  {
    id: "dashboard-card",
    label: "Dashboard campaign card",
    css: "linear-gradient(180deg,rgba(255,255,255,0.04),rgba(0,0,0,0.14),rgba(0,0,0,0.54))",
  },
  {
    id: "dashboard-top-wash",
    label: "Dashboard top edge wash",
    css: "linear-gradient(180deg,rgba(19,19,19,0.05),transparent)",
  },
  {
    id: "detail-hero",
    label: "Campaign detail — full-width hero",
    css: "linear-gradient(180deg,rgba(255,255,255,0.06),rgba(0,0,0,0.5))",
  },
  {
    id: "detail-split",
    label: "Campaign detail — split layout header",
    css: "linear-gradient(180deg,rgba(0,0,0,0.15),rgba(0,0,0,0.55))",
  },
] as const;

export const CAMPAIGN_GRADIENT_ANGLE = 135 as const;

export type CampaignGradientStop = { pct: number; hex: string };

export type CampaignGradientPreset = {
  id: string;
  label: string;
  /** Where this shows up or what it evokes — for the palette lab only. */
  note: string;
  stops: readonly CampaignGradientStop[];
};

/**
 * Deterministic campaign "posters": each ID hashes to one of these gradients.
 */
export const CAMPAIGN_GRADIENT_PRESETS: readonly CampaignGradientPreset[] = [
  {
    id: "russet-archive",
    label: "Russet archive",
    note: "Burnt sienna / terracotta into bright gold — the reddish hero look.",
    stops: [
      { pct: 0, hex: "#5d2f26" },
      { pct: 46, hex: "#9c4c32" },
      { pct: 100, hex: "#f0c26f" },
    ],
  },
  {
    id: "verdant-gold",
    label: "Verdant gold",
    note: "Deep pine into moss and harvest gold.",
    stops: [
      { pct: 0, hex: "#23443a" },
      { pct: 42, hex: "#3e664d" },
      { pct: 100, hex: "#d8b35f" },
    ],
  },
];

/** Every stop from {@link CAMPAIGN_GRADIENT_PRESETS}, in preset order — for promoting hexes into UI tokens. */
export const CAMPAIGN_GRADIENT_PALETTE_SWATCHES: readonly {
  hex: string;
  presetId: string;
  presetLabel: string;
  stopPct: number;
}[] = CAMPAIGN_GRADIENT_PRESETS.flatMap((preset) =>
  preset.stops.map((stop) => ({
    hex: stop.hex,
    presetId: preset.id,
    presetLabel: preset.label,
    stopPct: stop.pct,
  })),
);

export const GRADIENT_REVIEW_ANGLES = [0, 45, 90, 135, 180] as const;

export function buildCampaignGradientCss(
  stops: readonly CampaignGradientStop[],
  angleDeg: number = CAMPAIGN_GRADIENT_ANGLE,
): string {
  const stopList = stops.map((s) => `${s.hex} ${s.pct}%`).join(", ");
  return `linear-gradient(${angleDeg}deg, ${stopList})`;
}

const CAMPAIGN_GRADIENT_CSS_LIST = CAMPAIGN_GRADIENT_PRESETS.map((preset) =>
  buildCampaignGradientCss(preset.stops),
);

export function getCampaignGradient(campaignId: string): string {
  const hash = Array.from(campaignId).reduce((sum, character) => sum + character.charCodeAt(0), 0);
  return CAMPAIGN_GRADIENT_CSS_LIST[hash % CAMPAIGN_GRADIENT_CSS_LIST.length] ?? CAMPAIGN_GRADIENT_CSS_LIST[0];
}

export function getCampaignGradientPresetById(id: string): CampaignGradientPreset | undefined {
  return CAMPAIGN_GRADIENT_PRESETS.find((preset) => preset.id === id);
}

/** Labels + composed classes for the design system colors lab button gallery (source of truth for recipes). */
export const COLOR_LAB_BUTTON_DEMOS: readonly {
  id: string;
  title: string;
  description: string;
  variant: ButtonVariant;
  chrome: ButtonChrome;
}[] = [
  {
    id: "primary-affirmative",
    title: "Primary",
    description: "Default affirmative action — green fill for the main forward path.",
    variant: "primary",
    chrome: "frame",
  },
  {
    id: "destructive-warning",
    title: "Destructive",
    description: "Terracotta warning action for delete, remove, and other irreversible choices.",
    variant: "destructive",
    chrome: "frame",
  },
  {
    id: "cancel-neutral",
    title: "Cancel",
    description: "Low-emphasis neutral outline for backing out or dismissing a flow.",
    variant: "cancel",
    chrome: "frame",
  },
  {
    id: "secondary-paper",
    title: "Secondary",
    description: "Warm paper fill for supporting non-destructive actions that still need weight.",
    variant: "secondary",
    chrome: "frame",
  },
  {
    id: "muted-tertiary",
    title: "Muted",
    description: "Low-emphasis tertiary action for toolbars and lightweight utility controls.",
    variant: "muted",
    chrome: "frame",
  },
];
